import React, {useContext, useState} from 'react';

import {ContractChangeType} from 'src/openapi/admin/models/ContractChangeType';

import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';

import {getCredits, getDate} from '../../../../utils';
import {textAlign} from '../../../tabs-structure.module.styl';

import {CHANGELOG_CELL_WIDTH as WIDTH} from './constants';

import {apiV2ChangelogTable, apiV2ChangelogTableTable} from './changelog-table.module.styl';

import type {ContractChange} from 'src/openapi/admin/models/ContractChange';
import type {DataCommentInfo} from 'src/openapi/admin/models/DataCommentInfo';


const TYPE_LABEL = {
    [ContractChangeType.NEW_CONTRACT]: 'New Contract',
    [ContractChangeType.CREDIT_CHANGE]: 'Credit Change',
    [ContractChangeType.CONTRACT_EDIT]: 'Contract Edit',
    [ContractChangeType.CONTRACT_MOVED_TO_PAST]: 'Contract Edit',
    [ContractChangeType.CONTRACT_MOVED_TO_ACTIVE]: 'Contract Edit',
};

type Props = {
    data: Array<ContractChange>,
    creditsDataAvailable: boolean,
};

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const ChangelogTable = (props: Props) => {
    const {data, creditsDataAvailable} = props;

    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const {locale} = useContext(LocaleContext);

    const getCredit = (value: number | null, changeType: ContractChangeType) => {
        if (value === null) {
            return '-';
        }
        switch (changeType) {
            case ContractChangeType.CONTRACT_EDIT:
            case ContractChangeType.CONTRACT_MOVED_TO_PAST:
            case ContractChangeType.CONTRACT_MOVED_TO_ACTIVE:
                return '-';
            default:
        }
        return `${value > 0 ? '+' : ''}${getCredits(value, locale)}`;
    };

    const getComment = (dataCommentInfo: DataCommentInfo, changeType: ContractChangeType) => {
        // const {locale} = this.context;

        const commentsList = [];

        const {
            previousStartDate,
            currentStartDate,
            previousEndDate,
            currentEndDate,
            previousCredits,
            currentCredits,
            comment,
        } = dataCommentInfo;

        const prevS = getDate(previousStartDate, locale);
        const currS = getDate(currentStartDate, locale);
        const prevE = getDate(previousEndDate, locale);
        const currE = getDate(currentEndDate, locale);
        const prevC = getCredits(previousCredits, locale);
        const currC = getCredits(currentCredits, locale);

        if (changeType === 'NEW_CONTRACT') {
            commentsList.push(`Contract signed at ${currS}`);
        } else if (changeType === 'CONTRACT_MOVED_TO_PAST') {
            commentsList.push('Contract moved to Past Section');
        } else if (changeType === 'CONTRACT_MOVED_TO_ACTIVE') {
            commentsList.push('Contract moved to Active Section');
        } else {
            if (prevS && currS && prevS !== currS) {
                commentsList.push(`Start Date changed from ${prevS} to ${currS}`);
            }
            if (prevE && currE && prevE !== currE) {
                commentsList.push(`End Date changed from ${prevE} to ${currE}`);
            }
            if (prevC && currC) {
                commentsList.push(`Number of Credits changed from ${prevC} to ${currC}`);
            }
        }

        return (
            <Group direction="v" gap="xxs" alignItems="start">
                {commentsList.join(', ')}
                {comment ? `Comment: ${comment}` : null}
            </Group>
        );
    };

    const onPaginationChange = (newCurrentPage: number, newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(newCurrentPage);
    };

    const className = textAlign;

    const startIndex = pageSize * (currentPage - 1);
    const endIndex = startIndex + pageSize;
    const pageData = data.slice(startIndex, endIndex);

    return (
        <Group className={apiV2ChangelogTable} gap="s" direction="v" alignItems="start">
            <Typography fontWeight="semi-bold" variant="heading-level-5">Contract Changelog</Typography>
            <Table className={apiV2ChangelogTableTable}>
                <Head>
                    <Cell {...{className, width: WIDTH.date}}>Date</Cell>
                    <Cell {...{className, width: WIDTH.type}}>Type</Cell>
                    {creditsDataAvailable
                        ? <Cell {...{className: 'text-align_right', width: WIDTH.credit}}>Credit Change</Cell>
                        : null}
                    <Cell {...{className, width: WIDTH.separator}}/>
                    <Cell {...{className}}>Comments</Cell>
                    <Cell {...{className, width: WIDTH.changedBy}}>Change By</Cell>
                </Head>
                <Body>
                    {pageData.map((item, index) => {
                        const {createdAt, changeType, createdBy, dataCommentInfo} = item;
                        const {previousCredits = 0, currentCredits = 0} = dataCommentInfo;
                        return (
                            <Row key={index}>
                                <Cell>
                                    {getDate(createdAt, locale)}
                                </Cell>
                                <Cell>{TYPE_LABEL[changeType]}</Cell>
                                {creditsDataAvailable ? (
                                    <Cell {...{className: 'text-align_right'}}>
                                        {getCredit(currentCredits - previousCredits, changeType)}
                                    </Cell>
                                ) : null}
                                <Cell/>
                                <Cell {...{whiteSpaceNormal: true}}>
                                    {getComment(dataCommentInfo, changeType)}
                                </Cell>
                                <Cell>{createdBy}</Cell>
                            </Row>
                        );
                    })}
                </Body>
            </Table>
            <PaginationStripe
                totalCount={data.length}
                currentPage={currentPage}
                pageSize={pageSize}
                onPaginationChange={p => onPaginationChange(p.currentPage, p.pageSize)}
            />
        </Group>
    );
};
